import React, { Component, createRef } from 'react';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faCircleNotch, faUpload, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ProgressSpinner } from 'primereact/progressspinner';
import { isValidForm } from '../../utils/validation/CommonValidator';
import { CommonValidationMessages } from '../../utils/Messages';
import ValidationText from '../../utils/validation/ValidationText';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import SwalServices from '../../services/swalServices/SwalServices';
import { APIURL } from '../../services/axiosServices/ApiEndPoints';

export default class HouseMasterDetails extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.fileInputRef = createRef();
        this.state = {
            houseId: null,
            isLoading: false,
            isUploadingImage: false,
            route: null,
            tempImage: null,
            existingImage: '',
            houseForm: {
                housename: '',
                houseaddress: '',
                housecountry: '',
                housestate: '',
                housecity: '',
                housezip: '',
                housephone: '',
                houseemail: '',
                housecontactemail: '',
                housedescription: '',
            },
            validState: {
                isValid: true,
                error: {},
            },
            validationRules: {
                housename: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'house name'),
                    },
                ],
                houseaddress: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'house address'),
                    },
                ],
                housecity: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'city'),
                    },
                ],
                housestate: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'county/state'),
                    },
                ],
                housezip: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'postcode'),
                    },
                ],
                housecountry: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'country'),
                    },
                ],
                housephone: [
                    {
                        type: 'require',
                        message: CommonValidationMessages.FieldRequired.replace('{0}', 'phone number'),
                    },
                ],
            },
        };
    }

    componentDidMount() {
        const params = new URLSearchParams(window.location.search);
        const houseId = Number(params.get(''));
        if (houseId && houseId > 0) {
            this.setState({ houseId }, () => this.getHouseData(houseId));
        }
    }

    getHouseData = (id) => {
        this.adminServices.getHouseData(id).then((response) => {
            if (response.success === 1 && response.data) {
                const res = response.data;
                this.setState({
                    existingImage: res.houseimage || '',
                    houseForm: {
                        housename: res.housename || '',
                        houseaddress: res.houseaddress || '',
                        housecountry: res.housecountry || '',
                        housestate: res.housestate || '',
                        housecity: res.housecity || '',
                        housezip: res.housezip || '',
                        housephone: res.housephone || '',
                        houseemail: res.houseemail || '',
                        housecontactemail: res.housecontactemail || '',
                        housedescription: res.housedescription || '',
                    },
                });
            } else {
                this.swalServices.Error(response.msg);
            }
        });
    };

    handleChange = (event, name) => {
        const value = event.target.value;
        this.setState((prev) => ({
            houseForm: { ...prev.houseForm, [name]: value },
        }));
    };

    handleImageSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        const maxSize = 2 * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            this.swalServices.Error('Invalid file type. Please upload JPG, PNG, or GIF.');
            return;
        }
        if (file.size > maxSize) {
            this.swalServices.Error('File size exceeds 2MB. Please upload a smaller image.');
            return;
        }

        this.setState({ isUploadingImage: true });
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await this.adminServices.uploadTempImage(formData);
            if (response?.success === 1) {
                this.setState({
                    tempImage: {
                        tempPath: response.data.tempPath,
                        filename: response.data.filename,
                        preview: URL.createObjectURL(file),
                    },
                    existingImage: '',
                });
            } else {
                this.swalServices.Error(response?.msg || 'Image upload failed.');
            }
        } catch {
            this.swalServices.Error('Image upload failed. Please try again.');
        }

        this.setState({ isUploadingImage: false });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    };

    removeImage = () => {
        if (this.state.tempImage?.preview) {
            URL.revokeObjectURL(this.state.tempImage.preview);
        }
        this.setState({ tempImage: null, existingImage: '' });
    };

    isValidAllFields = () => {
        const newValidState = isValidForm(
            this.state.houseForm,
            this.state.validationRules,
            this.state.validState
        );
        this.setState({ validState: newValidState });
        return newValidState.isValid;
    };

    buildRequest = () => {
        const { houseForm, tempImage, existingImage } = this.state;
        return {
            housename: houseForm.housename,
            houseaddress: houseForm.houseaddress,
            housecountry: houseForm.housecountry,
            housestate: houseForm.housestate,
            housecity: houseForm.housecity,
            housezip: houseForm.housezip,
            housephone: houseForm.housephone,
            houseemail: houseForm.houseemail,
            housecontactemail: houseForm.housecontactemail,
            housedescription: houseForm.housedescription,
            tempImage: tempImage
                ? { tempPath: tempImage.tempPath, filename: tempImage.filename }
                : null,
            existingImage: existingImage || '',
        };
    };

    createHouse = () => {
        if (!this.isValidAllFields()) return;
        this.setState({ isLoading: true });
        this.adminServices.createHouse(this.buildRequest()).then((response) => {
            if (response.success === 1) {
                this.swalServices.Success(response.msg);
                this.setState({ route: '/house-master' });
            } else {
                this.swalServices.Error(response.msg);
            }
            this.setState({ isLoading: false });
        });
    };

    updateHouse = () => {
        if (!this.isValidAllFields()) return;
        this.setState({ isLoading: true });
        this.adminServices.updateHouse(this.buildRequest(), this.state.houseId).then((response) => {
            if (response.success === 1) {
                this.swalServices.Success(response.msg);
                this.setState({ route: '/house-master' });
            } else {
                this.swalServices.Error(response.msg);
            }
            this.setState({ isLoading: false });
        });
    };

    getImageSrc = () => {
        const { tempImage, existingImage } = this.state;
        if (tempImage?.preview) return tempImage.preview;
        if (existingImage) return `${APIURL.replace(/\/$/, '')}/uploads/house_images/${existingImage}`;
        return null;
    };

    render() {
        if (this.state.route != null) {
            return <Navigate to={this.state.route} />;
        }

        const { houseForm, validState, houseId, isLoading, isUploadingImage } = this.state;
        const imageSrc = this.getImageSrc();
        const hasImage = !!imageSrc;

        return (
            <div
                className="house-details"
                style={{ height: 'calc((100dvh / 0.85) - 45.8px)' }}
            >
                <div
                    className="house-details-inner p-[30px] overflow-y-auto"
                    style={{ height: '100%' }}
                >
                    <div className="title flex items-center justify-between mb-[10px]">
                        <h1
                            className="text-[22px] m-0 font-semibold text-[#004AAD]"
                            style={{ lineHeight: '30px' }}
                        >
                            {houseId > 0 ? 'Edit House' : 'Add New House'}
                        </h1>
                        <button
                            onClick={() => this.setState({ route: '/house-master' })}
                            className="flex items-center gap-[5px] py-[5px] px-[10px] text-[14px] font-medium rounded-[5px] text-[#004AAD] bg-white hover:bg-gray-100 border border-[#DFDFDF]"
                        >
                            <FontAwesomeIcon icon={faArrowLeftLong} />
                            Back to list
                        </button>
                    </div>

                    <div
                        className="house-form p-5 bg-white rounded-[16px]"
                        style={{ border: '1px solid #DFDFDF' }}
                    >
                        <h2 className="text-[16px] font-semibold mt-0">House Detail</h2>

                        <div className="grid grid-cols-2 gap-5">
                            {/* House Name */}
                            <div className="input-grp !mb-0">
                                <label>
                                    House Name <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter house name"
                                    name="housename"
                                    className={validState?.error?.housename ? 'error' : ''}
                                    value={houseForm.housename}
                                    onChange={(e) => this.handleChange(e, 'housename')}
                                />
                                <ValidationText error={validState.error.housename} />
                            </div>

                            {/* House Address */}
                            <div className="input-grp !mb-0">
                                <label>
                                    House Address <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter house address"
                                    name="houseaddress"
                                    className={validState?.error?.houseaddress ? 'error' : ''}
                                    value={houseForm.houseaddress}
                                    onChange={(e) => this.handleChange(e, 'houseaddress')}
                                />
                                <ValidationText error={validState.error.houseaddress} />
                            </div>

                            {/* City */}
                            <div className="input-grp !mb-0">
                                <label>
                                    City <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter city"
                                    name="housecity"
                                    className={validState?.error?.housecity ? 'error' : ''}
                                    value={houseForm.housecity}
                                    onChange={(e) => this.handleChange(e, 'housecity')}
                                />
                                <ValidationText error={validState.error.housecity} />
                            </div>

                            {/* County / State */}
                            <div className="input-grp !mb-0">
                                <label>
                                    County <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter county/state"
                                    name="housestate"
                                    className={validState?.error?.housestate ? 'error' : ''}
                                    value={houseForm.housestate}
                                    onChange={(e) => this.handleChange(e, 'housestate')}
                                />
                                <ValidationText error={validState.error.housestate} />
                            </div>

                            {/* Postcode */}
                            <div className="input-grp !mb-0">
                                <label>
                                    Postcode <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter postcode"
                                    name="housezip"
                                    className={validState?.error?.housezip ? 'error' : ''}
                                    value={houseForm.housezip}
                                    onChange={(e) => this.handleChange(e, 'housezip')}
                                />
                                <ValidationText error={validState.error.housezip} />
                            </div>

                            {/* Country */}
                            <div className="input-grp !mb-0">
                                <label>
                                    Country <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter country"
                                    name="housecountry"
                                    className={validState?.error?.housecountry ? 'error' : ''}
                                    value={houseForm.housecountry}
                                    onChange={(e) => this.handleChange(e, 'housecountry')}
                                />
                                <ValidationText error={validState.error.housecountry} />
                            </div>

                            {/* Phone */}
                            <div className="input-grp !mb-0">
                                <label>
                                    Phone No. <span className="text-[#c00000]">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter phone number"
                                    name="housephone"
                                    className={validState?.error?.housephone ? 'error' : ''}
                                    value={houseForm.housephone}
                                    onChange={(e) => this.handleChange(e, 'housephone')}
                                />
                                <ValidationText error={validState.error.housephone} />
                            </div>

                            {/* House Contact Email */}
                            <div className="input-grp !mb-0">
                                <label>House Email</label>
                                <input
                                    type="email"
                                    placeholder="e.g. info@housename.com"
                                    name="housecontactemail"
                                    className={validState?.error?.housecontactemail ? 'error' : ''}
                                    value={houseForm.housecontactemail}
                                    onChange={(e) => this.handleChange(e, 'housecontactemail')}
                                />
                                <ValidationText error={validState.error.housecontactemail} />
                            </div>

                            {/* Empty col to keep grid aligned before full-width notification emails */}
                            <div className="hidden" />

                            {/* Notification Emails — full width, comma-separated */}
                            <div className="input-grp !mb-0 col-span-2">
                                <label>Notification Emails</label>
                                <textarea
                                    rows={3}
                                    placeholder="e.g. manager@example.com, admin@example.com"
                                    name="houseemail"
                                    className={validState?.error?.houseemail ? 'error' : ''}
                                    value={houseForm.houseemail}
                                    onChange={(e) => this.handleChange(e, 'houseemail')}
                                    style={{ resize: 'vertical' }}
                                />
                                <p className="text-[12px] text-gray-500 mt-[4px] mb-0">
                                    These receive visitor arrival notifications. New addresses added on update will be notified.
                                </p>
                                <ValidationText error={validState.error.houseemail} />
                            </div>

                            {/* Description — full width */}
                            <div className="input-grp !mb-0 col-span-2">
                                <label>Description</label>
                                <textarea
                                    rows={4}
                                    placeholder="Enter a brief description of the house"
                                    name="housedescription"
                                    value={houseForm.housedescription}
                                    onChange={(e) => this.handleChange(e, 'housedescription')}
                                    style={{ resize: 'vertical' }}
                                />
                            </div>

                            {/* House Layout Image — full width */}
                            <div className="col-span-2">
                                <label className="block text-[14px] font-medium text-gray-700 mb-2">
                                    House Layout Image
                                </label>

                                {/* Preview */}
                                <div className="relative w-full h-[240px] rounded-[12px] bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 mb-3">
                                    {hasImage ? (
                                        <>
                                            <img
                                                src={imageSrc}
                                                alt="House layout"
                                                className="max-w-full max-h-full object-contain"
                                            />
                                            <button
                                                type="button"
                                                onClick={this.removeImage}
                                                className="absolute top-3 right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center border-0 shadow"
                                                title="Remove image"
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="text-[12px]" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="text-gray-400 text-center pointer-events-none">
                                            <FontAwesomeIcon icon={faUpload} className="text-4xl mb-2" />
                                            <p className="text-sm m-0">No image uploaded</p>
                                        </div>
                                    )}
                                </div>

                                {/* Upload button */}
                                <label className="inline-flex items-center gap-2 cursor-pointer py-[7px] px-[16px] text-[13px] font-medium rounded-[6px] text-white bg-[#0077B6] hover:bg-[#3b82f6] border-0">
                                    {isUploadingImage ? (
                                        <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" />
                                    ) : (
                                        <FontAwesomeIcon icon={faUpload} />
                                    )}
                                    {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/gif"
                                        onChange={this.handleImageSelect}
                                        ref={this.fileInputRef}
                                        className="hidden"
                                        disabled={isUploadingImage}
                                    />
                                </label>
                                <p className="text-[12px] text-gray-500 mt-[6px] mb-0">
                                    Upload a floor plan or layout image of the house. Accepted formats: JPG, PNG, GIF. Max size: 2MB.
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-[10px] mt-6 justify-end">
                            <button
                                onClick={() => this.setState({ route: '/house-master' })}
                                className="py-[7px] px-[20px] text-[14px] font-medium rounded-[5px] text-[#223645] bg-white hover:bg-gray-100 border border-[#DFDFDF]"
                            >
                                Cancel
                            </button>
                            {houseId > 0 ? (
                                <button
                                    onClick={this.updateHouse}
                                    disabled={isLoading || isUploadingImage}
                                    className="flex items-center gap-[5px] py-[7px] px-[20px] text-[14px] font-medium rounded-[5px] text-[#FFFFFF] bg-[#0077B6] hover:bg-[#3b82f6] border-0 disabled:opacity-60"
                                >
                                    {isLoading && <FontAwesomeIcon icon={faCircleNotch} spin />}
                                    Update
                                </button>
                            ) : (
                                <button
                                    onClick={this.createHouse}
                                    disabled={isLoading || isUploadingImage}
                                    className="flex items-center gap-[5px] py-[7px] px-[20px] text-[14px] font-medium rounded-[5px] text-[#FFFFFF] bg-[#0077B6] hover:bg-[#3b82f6] border-0 disabled:opacity-60"
                                >
                                    {isLoading && <FontAwesomeIcon icon={faCircleNotch} spin />}
                                    Save
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
